/**
 * Service Worker Manager
 * Handles conditional service worker registration based on browser compatibility
 */

import { isPushCapableBrowser } from './browserDetection';

export interface ServiceWorkerConfig {
  skipInAppBrowsers?: boolean;
  skipProblematicBrowsers?: boolean;
  retryAttempts?: number;
  retryDelay?: number;
}

/**
 * Build the service worker URL with the current environment's Firebase config
 * attached as query params.
 *
 * The worker used to hardcode the ehjzlee-qa project while the page ran against
 * ehjzlee-prod-f680b, so its messagingSenderId never matched and getToken() failed.
 * Passing config through the URL keeps one worker file across all environments.
 * A changed script URL for the same scope also replaces the existing registration,
 * which evicts any stale worker still installed on returning users' devices.
 */
const buildSwUrl = (swPath: string): string => {
  const params = new URLSearchParams({
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY ?? '',
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN ?? '',
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID ?? '',
    storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET ?? '',
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID ?? '',
    appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID ?? '',
  });

  return `${swPath}?${params.toString()}`;
};

export class ServiceWorkerManager {
  private static isRegistered = false;
  private static registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

  /**
   * Check if service worker should be registered based on browser conditions
   */
  static shouldRegister(config: ServiceWorkerConfig = {}): boolean {
    const {
      skipInAppBrowsers = true,
      skipProblematicBrowsers = true,
    } = config;

    // Skip if not in browser environment
    if (typeof window === 'undefined') {
      return false;
    }

    // Skip if service workers are not supported
    if (!('serviceWorker' in navigator)) {
      console.warn('Service worker not supported');
      return false;
    }

    // Skip if not in secure context
    if (!window.isSecureContext) {
      console.warn('Service worker requires secure context (HTTPS)');
      return false;
    }

    // Both flags now collapse onto one capability check: in-app browsers and
    // browsers without push support are exactly the ones we must skip.
    if ((skipInAppBrowsers || skipProblematicBrowsers) && !isPushCapableBrowser()) {
      console.log('Skipping service worker registration - browser cannot support push');
      return false;
    }

    return true;
  }

  /**
   * Register service worker with proper error handling and retry logic
   */
  static async register(
    swPath: string = '/firebase-messaging-sw.js',
    config: ServiceWorkerConfig = {}
  ): Promise<ServiceWorkerRegistration | null> {
    // Return existing registration promise if already in progress
    if (this.registrationPromise) {
      return this.registrationPromise;
    }

    // Create new registration promise
    this.registrationPromise = this._performRegistration(swPath, config);
    return this.registrationPromise;
  }

  /**
   * Internal method to perform the actual registration
   */
  private static async _performRegistration(
    swPath: string,
    config: ServiceWorkerConfig
  ): Promise<ServiceWorkerRegistration | null> {
    const {
      retryAttempts = 3,
      retryDelay = 1000,
    } = config;

    // Check if we should register
    if (!this.shouldRegister(config)) {
      return null;
    }

    // Skip if already registered
    if (this.isRegistered) {
      const existingRegistration = await navigator.serviceWorker.getRegistration();
      return existingRegistration || null;
    }

    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= retryAttempts; attempt++) {
      try {
        console.log(`Attempting service worker registration (attempt ${attempt}/${retryAttempts})`);
        
        const registration = await navigator.serviceWorker.register(buildSwUrl(swPath), {
          scope: '/',
          updateViaCache: 'imports',
        });

        this.isRegistered = true;
        console.log('Service worker registered successfully:', registration);

        // Set up update handling
        this.setupUpdateHandling(registration);

        return registration;
      } catch (error) {
        lastError = error as Error;
        console.warn(`Service worker registration attempt ${attempt} failed:`, error);

        // Don't retry on certain errors
        if (this.isNonRetryableError(error)) {
          break;
        }

        // Wait before retry (except on last attempt)
        if (attempt < retryAttempts) {
          await this.delay(retryDelay * attempt);
        }
      }
    }

    console.error('Service worker registration failed after all attempts:', lastError);
    return null;
  }

  /**
   * Check if error should not be retried
   */
  private static isNonRetryableError(error: any): boolean {
    const errorMessage = error?.message?.toLowerCase() || '';
    
    // Don't retry these types of errors
    const nonRetryablePatterns = [
      'script evaluation failed',
      'importscripts failed',
      'security error',
      'not allowed',
      'permission denied',
    ];

    return nonRetryablePatterns.some(pattern => errorMessage.includes(pattern));
  }

  /**
   * Set up service worker update handling
   */
  private static setupUpdateHandling(registration: ServiceWorkerRegistration): void {
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) return;

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          console.log('New service worker available, will activate on next page load');
          // You can dispatch a custom event here to notify the app about updates
          window.dispatchEvent(new CustomEvent('sw-update-available', {
            detail: { registration }
          }));
        }
      });
    });
  }

  /**
   * Unregister service worker
   */
  static async unregister(): Promise<boolean> {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        const success = await registration.unregister();
        this.isRegistered = false;
        this.registrationPromise = null;
        console.log('Service worker unregistered:', success);
        return success;
      }
      return true;
    } catch (error) {
      console.error('Error unregistering service worker:', error);
      return false;
    }
  }

  /**
   * Get current registration
   */
  static async getRegistration(): Promise<ServiceWorkerRegistration | null> {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return null;
    }

    try {
      return await navigator.serviceWorker.getRegistration() || null;
    } catch (error) {
      console.error('Error getting service worker registration:', error);
      return null;
    }
  }

  /**
   * Utility method for delays
   */
  private static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check if service worker is currently registered
   */
  static get isServiceWorkerRegistered(): boolean {
    return this.isRegistered;
  }
}

/**
 * Legacy function for backward compatibility
 * @deprecated Use ServiceWorkerManager.register() instead
 */
export const registerServiceWorker = (swPath?: string, config?: ServiceWorkerConfig) => {
  return ServiceWorkerManager.register(swPath, config);
};

export default ServiceWorkerManager;
